"use client"

import XIconThin from "@/components/shared/icons/XIconThin"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import { Dialog, IconButton, Typography } from "@mui/material"
import Image from "next/image"
import NewsletterForm from "./NewsletterForm"
import {
  NEWSLETTER_POPUP_IMAGE_ALT,
  NEWSLETTER_POPUP_IMAGE_HEIGHT,
  NEWSLETTER_POPUP_IMAGE_WIDTH,
  ResolvedNewsletterPopup,
} from "./newsletterContent"

const StyledDialog = styled(Dialog)`
  & .MuiPaper-root {
    background-color: ${colors.directBlack};
    border-radius: 8px;
    box-shadow: 0 4px 5px rgba(0, 0, 0, 0.1);
    padding: 16px 32px 32px;
    margin: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: 100%;
    max-width: 358px;
    ${({ theme }) => theme.breakpoints.up("md")} {
      max-width: 573px;
    }
  }
`

const CloseRow = styled.div`
  display: flex;
  justify-content: flex-end;
  direction: ltr;
  width: 100%;
`

const CloseButton = styled(IconButton)`
  padding: 0;
  width: 24px;
  height: 24px;
`

const PopupImage = styled.div`
  position: relative;
  width: 100%;
  flex-shrink: 0;
  aspect-ratio: ${NEWSLETTER_POPUP_IMAGE_WIDTH} / ${NEWSLETTER_POPUP_IMAGE_HEIGHT};
  border-radius: 12px;
  overflow: hidden;
  img {
    object-fit: cover;
  }
`

const Headline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const Heading = styled(Typography)`
  color: ${colors.white};
  font-size: 24px;
  font-weight: 400;
  line-height: normal;
  text-align: center;
`

const Subtitle = styled(Typography)`
  color: ${colors.white};
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  text-align: center;
`

interface NewsletterPopupProps {
  open: boolean
  source: string
  content: ResolvedNewsletterPopup
  onClose: () => void
  onSuccess: () => void
}

const NewsletterPopup = ({
  open,
  source,
  content,
  onClose,
  onSuccess,
}: NewsletterPopupProps): React.ReactElement => {
  return (
    <StyledDialog open={open} onClose={onClose} dir="rtl">
      <CloseRow>
        <CloseButton onClick={onClose} aria-label="סגירה">
          <XIconThin color={colors.white} />
        </CloseButton>
      </CloseRow>
      <PopupImage>
        <Image
          src={content.imageUrl}
          alt={NEWSLETTER_POPUP_IMAGE_ALT}
          fill
          sizes="(max-width: 767px) 100vw, 509px"
        />
      </PopupImage>
      <Headline>
        <Heading variant="h3">{content.title}</Heading>
        {!!content.subtitle && <Subtitle variant="body2">{content.subtitle}</Subtitle>}
      </Headline>
      <NewsletterForm source={source} onSuccess={onSuccess} layout="popup" />
    </StyledDialog>
  )
}

export default NewsletterPopup
